import { Link } from 'react-router-dom'
import SEOHead from '../components/seo/SEOHead'
import { LEGAL_CONTACT_EMAIL } from '../config/legal'

export default function FairUsePolicy() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-navy-900 py-16">
      <SEOHead
        title="Fair Use & Access Policy | learn.rondanini.com"
        description="How subscribers may use content, tools, and materials on learn.rondanini.com."
        canonicalUrl="https://learn.rondanini.com/fair-use"
        ogType="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Fair Use & Access Policy',
          url: 'https://learn.rondanini.com/fair-use',
          description:
            'How subscribers may use content, tools, and materials on learn.rondanini.com.',
          publisher: {
            '@type': 'Organization',
            name: 'Rondanini Publishing Ltd',
            url: 'https://learn.rondanini.com',
          },
        }}
      />
      <article className="max-w-4xl mx-auto px-4 container-padding prose prose-lg dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-li:text-gray-700 dark:prose-li:text-gray-300">
        <h1>Fair Use &amp; Access Policy</h1>
        <p>
          <em>Last updated: 26 March 2026 | Rondanini Publishing Ltd</em>
        </p>

        <h2>1. Who this applies to</h2>
        <p>
          This policy applies to all users who access content, tools, calculators, courses, or materials on
          learn.rondanini.com (&quot;the Platform&quot;), operated by Rondanini Publishing Ltd (registered in England
          &amp; Wales).
        </p>

        <h2>2. What your subscription gives you</h2>
        <p>
          Your active subscription grants you a personal, non-exclusive, non-transferable licence to access and use the
          materials available under your chosen plan for your own private study and professional reference. This licence
          is for one named individual only.
        </p>

        <h2>3. What you may do</h2>
        <ul>
          <li>Access all content included in your plan during your subscription period.</li>
          <li>Download or export materials explicitly marked as downloadable.</li>
          <li>Take personal notes, annotate, and screenshot content for your own private use.</li>
          <li>
            Share a single short excerpt (no more than 50 words or one chart) for the purpose of fair comment, review,
            or professional discussion, provided you attribute it to learn.rondanini.com.
          </li>
        </ul>

        <h2>4. What you may not do</h2>
        <ul>
          <li>
            <strong>Share your login credentials</strong> with any other person.
          </li>
          <li>
            <strong>Redistribute, republish, or resell</strong> any content, tools, calculators, or materials from the
            Platform, whether in original or modified form.
          </li>
          <li>
            <strong>Copy or reproduce</strong> substantial portions of any course, module, or written material for
            commercial purposes or public distribution.
          </li>
          <li>
            <strong>Screenshot or record</strong> content for the purpose of sharing, distributing, or creating
            derivative products without our prior written permission.
          </li>
          <li>
            <strong>Use AI tools</strong> to systematically extract, summarise, or reproduce Platform content at scale.
          </li>
          <li>
            <strong>Circumvent</strong> any access controls, watermarks, or technical protection measures on the Platform.
          </li>
          <li>
            <strong>Sublicense</strong> access to a team or organisation under a personal subscription (contact us for
            team pricing).
          </li>
        </ul>

        <h2>5. Screenshots and personal notes</h2>
        <p>
          We do not technically prevent screenshots. We rely on this policy and your consent at checkout. Taking
          screenshots or screen recordings for your own private study is permitted. Sharing them publicly, posting
          them to social media, or using them in any commercial context is not permitted and may constitute copyright
          infringement under the Copyright, Designs and Patents Act 1988.
        </p>
        <p>
          Where content is exported as PDF, files may contain embedded metadata identifying your account. This is used
          solely to investigate suspected misuse and is not shared with third parties.
        </p>

        <h2>6. Account identity and audit</h2>
        <p>
          We reserve the right to log access patterns to identify account sharing or systematic content extraction. If we
          reasonably suspect a breach of this policy, we may suspend access pending investigation and, if the breach is
          confirmed, terminate the account without refund.
        </p>

        <h2>7. Reporting misuse</h2>
        <p>
          If you believe Platform content is being used without authorisation, please contact us at{' '}
          <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>.
        </p>

        <h2>8. Changes to this policy</h2>
        <p>
          We may update this policy from time to time. Continued use of the Platform after notice of changes constitutes
          acceptance of the revised policy. Material changes will be notified by email to active subscribers.
        </p>

        <h2>9. Governing law</h2>
        <p>
          This policy forms part of your agreement with Rondanini Publishing Ltd and is governed by the laws of England
          and Wales.
        </p>

        <p className="not-prose text-sm text-gray-500 dark:text-gray-400 pt-8 border-t border-gray-200 dark:border-navy-600">
          <Link to="/" className="text-gold-600 dark:text-gold-400 hover:underline">
            ← Back to home
          </Link>
        </p>
      </article>
    </div>
  )
}
